import { Lightning, Registry, Router } from '@lightningjs/sdk'

import SpeechComp from '../../components/speechComp/speechComp'
import { HIDDEN } from '../../components/TvCards/templates'
import { getColor, grey300, grey960, white } from '../../constants/colors'
import { UniNeueHeavy, UniNeueRegular } from '../../constants/fonts'
import { Images } from '../../constants/paths'
import { BASE_ROUTES } from '../../constants/routes'
import { getText } from '../../utils/dictionary'
import { getAsset } from '../../utils/image'

interface HistoryState {
  /** Stores the focused ref when navigating away. */
  lastFocus?: string
}

const ROTATION_TIME_OUT = 2500
const INITIAL_FADE_IN = 0.25 // seconds

const IMAGE_ROTATION = [
  'https://d6fm3yzmawlcs.cloudfront.net/images/BG_1+2.png',
  'https://d6fm3yzmawlcs.cloudfront.net/images/BG_3+1.png',
  'https://d6fm3yzmawlcs.cloudfront.net/images/BG_4+1.png',
  'https://d6fm3yzmawlcs.cloudfront.net/images/BG_5+1.png',
  'https://d6fm3yzmawlcs.cloudfront.net/images/BG_6+1.png',
]

export default class BaseLanding extends SpeechComp {
  protected _contextData: SubscriptionContextData
  protected _currentButton
  private _currentImgIndex = 0
  private _firstLoad = false
  private _rotationStartTimeout = -1

  static _template() {
    return {
      w: 1920,
      h: 1080,
      react: true,
      color: getColor(grey960),
      Images: {
        w: (w) => w,
        h: (h) => h,
        children: IMAGE_ROTATION.map((img) => ({
          w: (w) => w,
          h: (h) => h,
          src: img,
          alpha: HIDDEN,
        })),
      },
      Gradient: {
        w: (w) => w,
        h: (h) => h,
        src: getAsset(Images.LANDING_BG),
      },
      Logo: {
        w: 246,
        h: 28,
        y: 48,
        x: 137,
        texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGO_LARGE), 246, 28),
      },
      Title: {
        w: 796,
        y: 580,
        x: 137,
        text: {
          text: this.getTitleText(),
          textColor: getColor(white),
          fontSize: 64,
          fontFace: UniNeueHeavy,
          lineHeight: 72,
        },
      },
      Subtitle: {
        w: 796,
        h: 72,
        y: 748,
        x: 137,
        text: {
          text: this.getSubtitleText(),
          textColor: getColor(grey300),
          fontSize: 32,
          fontFace: UniNeueRegular,
          lineHeight: 32,
        },
      },
    }
  }

  get firstImage() {
    return this.images.childList.getAt(0)
  }

  get gradient() {
    return this.tag('Gradient')
  }

  get images() {
    return this.tag('Images')
  }

  get pageText() {
    return 'Landing'
  }

  get sectionText(): string {
    return getText('landingPageTitle') + ' . . ' + getText('landingPageSubtitle')
  }

  static getTitleText() {
    return getText('landingPageTitle')
  }

  static getSubtitleText() {
    return getText('landingPageSubtitle')
  }

  set params(v: SubscriptionContextData) {
    this._contextData = v
  }

  _init() {
    // We wait for the gradient and first image to be
    // loaded before starting the image rotation
    this.gradient.on('txLoaded', () => {
      if (this._firstLoad) return

      this._firstLoad = true
      this.images.children[this._currentImgIndex].setSmooth('alpha', 1, {
        duration: INITIAL_FADE_IN,
      })

      this._rotationStartTimeout = Registry.setTimeout(() => {
        this.startImgRotation()
      }, ROTATION_TIME_OUT)
    })
  }

  _active() {
    this._refocus()
  }

  _getFocused() {
    return this._currentButton
  }

  _handleBack() {
    const history = Router.getHistory()
    if (history[history.length - 1]?.hash?.includes(BASE_ROUTES.PLAYER)) {
      Router.go(-2)
    } else if (history.length > 0) {
      Router.back()
    } else {
      Router.focusWidget('ExitWindow')
    }
  }

  _inactive() {
    Registry.clearTimeout(this._rotationStartTimeout)
  }

  historyState(params: HistoryState) {
    if (params?.lastFocus) {
      this._currentButton = this.tag(params.lastFocus)
    } else {
      return {
        lastFocus: this._getFocused()?.ref,
      }
    }
  }

  resetAlphaExcept(activeIndex) {
    const children = this.images.children
    children.forEach((img, i) => {
      if (i !== activeIndex) {
        img.transition('alpha').stop && img.transition('alpha').stop()
        img.alpha = HIDDEN
      }
    })
  }

  startImgRotation() {
    const images = this.images.children
    const currentImg = images[this._currentImgIndex]

    // determine next index
    const nextIndex = (this._currentImgIndex + 1) % images.length
    const nextImg = images[nextIndex]

    // ensure all other images are hidden, this prevents the pop up effect
    // after the first cycle because the images already have alpha = 1
    this.resetAlphaExcept(this._currentImgIndex)

    // fade out current
    const onFadeOutFinish = () => {
      currentImg.transition('alpha').off('finish', onFadeOutFinish)

      // fade in next
      const onFadeInFinish = () => {
        nextImg.transition('alpha').off('finish', onFadeInFinish)
        this._currentImgIndex = nextIndex

        // schedule next rotation
        this._rotationStartTimeout = Registry.setTimeout(
          () => this.startImgRotation(),
          ROTATION_TIME_OUT,
        )
      }

      nextImg.transition('alpha').on('finish', onFadeInFinish)
      nextImg.setSmooth('alpha', 1, { duration: 2.5 })
    }

    currentImg.transition('alpha').on('finish', onFadeOutFinish)
    currentImg.setSmooth('alpha', HIDDEN, { duration: 2.5 })
  }
}
